import React, { useCallback, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "@fontsource/plus-jakarta-sans";

/**
 * Planner Admin Panel
 * --------------------
 * Manages two backend resources via the admin endpoints:
 *   planner_config  -> /planner/admin/config        (key / value)
 *   planner_master  -> /planner/admin/master?type=  (tours / meals / etc.)
 *
 * REACT_APP_API_VMS_URL already includes the /v1 prefix, so the paths below
 * resolve to /v1/planner/admin/... on the server.
 */

const API_BASE = (process.env.REACT_APP_API_VMS_URL ?? "").replace(/\/+$/, "");

// ---------------------------------------------------------------------------
// Frontend-only auth (no backend). A single hardcoded credential unlocks the
// panel; the session is kept in localStorage and expires after 8 hours.
// ---------------------------------------------------------------------------
const ADMIN_USERNAME = "admin";
const ADMIN_PASSWORD = "admin@visitPortal2026";
const AUTH_STORAGE_KEY = "plannerAdminAuthExpiry";
const AUTH_TTL_MS = 8 * 60 * 60 * 1000; // 8 hours

function isAuthValid() {
  const expiry = Number(localStorage.getItem(AUTH_STORAGE_KEY));
  return Number.isFinite(expiry) && expiry > Date.now();
}

const MASTER_TYPES = [
  "DURATION_OPTION",
  "TOUR",
  "MUST_VISIT",
  "FREE_TIME",
  "VIDEO",
  "NEAR_BY",
];

// Friendly labels for the sidebar.
const TYPE_LABELS = {
  DURATION_OPTION: "Duration Options",
  TOUR: "Tours",
  MUST_VISIT: "Must Visit",
  FREE_TIME: "Free Time",
  VIDEO: "Videos",
  NEAR_BY: "Near By",
};

const WEEKDAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

// Columns the backend whitelists for planner_master. Keeping this list here
// lets us build the form generically and only send known fields.
const MASTER_FIELDS = [
  { key: "type", label: "Type", input: "select", options: MASTER_TYPES, required: true },
  { key: "item_key", label: "Item Key", input: "text", required: true },
  { key: "title", label: "Title", input: "text", required: true },
  { key: "start_time", label: "Start Time (HH:MM)", input: "text", placeholder: "09:00" },
  { key: "end_time", label: "End Time (HH:MM)", input: "text", placeholder: "10:00" },
  { key: "weekday", label: "Weekday", input: "select", options: ["", ...WEEKDAYS] },
  { key: "duration_mins", label: "Duration (mins)", input: "number" },
  { key: "venue", label: "Venue", input: "textarea" },
  { key: "note", label: "Note", input: "textarea" },
  { key: "image_path", label: "Image Path", input: "text" },
  { key: "is_open_area", label: "Open Area", input: "bit" },
  { key: "order_id", label: "Order", input: "number" },
  { key: "is_active", label: "Active", input: "bit" },
];

// Per-type field visibility for the create/edit modal. The value is the list
// of field keys (from MASTER_FIELDS) to show, in order. Any type NOT listed
// here falls back to showing ALL fields (current behaviour, e.g. TOUR).
//
// Note: a type kept out of its own list (like `type`) is still carried in the
// form and sent to the API — it's just not rendered. `item_key` is likewise
// auto-generated from the title when it isn't shown (see save()).
//
// To restrict another type later, just add an entry here.
const TYPE_MODAL_FIELDS = {
  DURATION_OPTION: ["title", "duration_mins", "order_id", "is_active"],
  TOUR: ["title", "start_time", "weekday", "order_id", "is_active"],
  MUST_VISIT: ["title", "duration_mins", "venue", "order_id", "is_active"],
  FREE_TIME: ["title", "start_time", "end_time", "duration_mins", "venue", "order_id", "is_open_area", "is_active"],
  VIDEO: ["title", "start_time", "end_time", "venue", "order_id", "is_active"],
  NEAR_BY: ["title", "start_time", "end_time", "duration_mins", "venue", "order_id","note", "is_active"],
};

// Resolve the ordered list of MASTER_FIELDS to render for a given type.
function getModalFields(type) {
  const keys = TYPE_MODAL_FIELDS[type];
  if (!keys) return MASTER_FIELDS; // default: show everything
  return keys.map((k) => MASTER_FIELDS.find((f) => f.key === k)).filter(Boolean);
}

// "Half Day" -> "HALF_DAY". Used to derive item_key when it isn't shown.
function slugifyKey(s) {
  return String(s ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const EMPTY_MASTER = {
  type: "TOUR",
  item_key: "",
  title: "",
  start_time: "",
  end_time: "",
  weekday: "",
  duration_mins: "",
  venue: "",
  note: "",
  image_path: "",
  is_open_area: 0,
  order_id: 0,
  is_active: 1,
};

// ----------------------------------------------------------------------------
// Networking helper. Normalises the various success/error shapes the API may
// return and throws with a readable message so callers can just toast it.
// ----------------------------------------------------------------------------
async function apiFetch(path, { method = "GET", body } = {}) {
  if (!API_BASE) throw new Error("REACT_APP_API_VMS_URL is not configured");

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  let json = null;
  try {
    json = await res.json();
  } catch {
    /* DELETE / empty responses may not carry a body */
  }

  if (!res.ok || json?.isSuccess === false) {
    const msg = json?.message || json?.error || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  // Unwrap a `data` envelope when present, else return the raw payload.
  return json?.data ?? json;
}

// Pull an array out of whatever the list endpoint returns.
function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.config)) return payload.config;
  if (Array.isArray(payload?.master)) return payload.master;
  return [];
}

// Empty string -> null so nullable columns can be cleared; bit fields kept as
// 0/1; numeric strings coerced to numbers.
function cleanMasterPayload(form) {
  const out = {};
  for (const f of MASTER_FIELDS) {
    const raw = form[f.key];
    if (f.input === "bit") {
      out[f.key] = raw ? 1 : 0;
    } else if (f.input === "number") {
      out[f.key] = raw === "" || raw == null ? null : Number(raw);
    } else {
      out[f.key] = raw === "" ? null : raw;
    }
  }
  return out;
}

const card = "bg-white rounded-xl shadow-sm border border-gray-200";
const btnBase =
  "inline-flex items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed";

const PlannerAdmin = () => {
  // 'CONFIG' or one of MASTER_TYPES — drives what the main area shows.
  const [view, setView] = useState("CONFIG");
  const [authed, setAuthed] = useState(() => isAuthValid());

  // Re-check the session on mount and whenever it would expire so the panel
  // locks itself back up after 8 hours without a manual refresh.
  useEffect(() => {
    if (!authed) return;
    const expiry = Number(localStorage.getItem(AUTH_STORAGE_KEY));
    const remaining = expiry - Date.now();
    if (remaining <= 0) {
      setAuthed(false);
      return;
    }
    const timer = setTimeout(() => setAuthed(false), remaining);
    return () => clearTimeout(timer);
  }, [authed]);

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuthed(false);
  };

  if (!authed) {
    return <PlannerLogin onSuccess={() => setAuthed(true)} />;
  }

  const navItem = (id, label) => (
    <button
      key={id}
      onClick={() => setView(id)}
      className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition ${
        view === id
          ? "bg-indigo-600 text-white shadow-sm"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-['Plus_Jakarta_Sans']">
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-60 shrink-0 bg-white border-r border-gray-200 flex flex-col">
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-lg font-bold text-gray-900">Planner Admin</h1>
                <p className="text-xs text-gray-500">Plan-a-visit data</p>
              </div>
              <button
                onClick={logout}
                className="text-xs font-medium text-gray-500 hover:text-red-600 transition"
              >
                Logout
              </button>
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            <p className="px-3 pt-1 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Configuration
            </p>
            {navItem("CONFIG", "Config (key / value)")}

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Master Data
            </p>
            {MASTER_TYPES.map((t) => navItem(t, TYPE_LABELS[t] || t))}
          </nav>
        </aside>

        {/* Main area */}
        <main className="flex-1 overflow-x-auto p-6">
          {!API_BASE && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              REACT_APP_API_VMS_URL is not set — API calls will fail.
            </div>
          )}
          {view === "CONFIG" ? (
            <ConfigTab />
          ) : (
            <MasterTab key={view} type={view} />
          )}
        </main>
      </div>
    </div>
  );
};

// ============================================================================
// CONFIG TAB
// ============================================================================
function ConfigTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row being edited, or 'new'
  const [draft, setDraft] = useState({ config_key: "", config_value: "" });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // row pending delete

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/planner/admin/config");
      setRows(toArray(data));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditing("new");
    setDraft({ config_key: "", config_value: "" });
  };
  const startEdit = (row) => {
    setEditing(row.id);
    setDraft({ config_key: row.config_key, config_value: row.config_value ?? "" });
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (editing === "new" && !draft.config_key.trim()) {
      toast.error("config_key is required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await apiFetch("/planner/admin/config", {
          method: "POST",
          body: {
            config_key: draft.config_key.trim(),
            config_value: draft.config_value,
          },
        });
        toast.success("Config created");
      } else {
        await apiFetch(`/planner/admin/config/${editing}`, {
          method: "PUT",
          body: { config_value: draft.config_value },
        });
        toast.success("Config updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const row = confirm;
    if (!row) return;
    try {
      await apiFetch(`/planner/admin/config/${row.id}`, { method: "DELETE" });
      toast.success("Config deleted");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <section className={`${card} overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="font-semibold text-gray-800">
          Configuration{" "}
          <span className="text-gray-400 font-normal">({rows.length})</span>
        </h2>
        <div className="flex gap-2">
          <button onClick={load} className={`${btnBase} text-gray-600 hover:bg-gray-100`}>
            Refresh
          </button>
          <button
            onClick={startNew}
            className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}
          >
            + Add Config
          </button>
        </div>
      </div>

      {editing === "new" && (
        <div className="bg-indigo-50/50 px-4 py-3 border-b border-gray-200 flex flex-wrap items-end gap-3">
          <Field label="Config Key" className="flex-1 min-w-[200px]">
            <input
              autoFocus
              value={draft.config_key}
              onChange={(e) => setDraft({ ...draft, config_key: e.target.value })}
              placeholder="END_OF_DAY_TIME"
              className={inputCls}
            />
          </Field>
          <Field label="Config Value" className="flex-1 min-w-[200px]">
            <input
              value={draft.config_value}
              onChange={(e) => setDraft({ ...draft, config_value: e.target.value })}
              placeholder="21:00"
              className={inputCls}
            />
          </Field>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={cancel} className={`${btnBase} text-gray-600 hover:bg-gray-100`}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-gray-400">No config entries.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">ID</th>
                <th className="px-4 py-2 font-medium">Key</th>
                <th className="px-4 py-2 font-medium">Value</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const isEdit = editing === row.id;
                return (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-400">{row.id}</td>
                    <td className="px-4 py-2 font-medium text-gray-800">{row.config_key}</td>
                    <td className="px-4 py-2">
                      {isEdit ? (
                        <input
                          autoFocus
                          value={draft.config_value}
                          onChange={(e) => setDraft({ ...draft, config_value: e.target.value })}
                          className={inputCls}
                        />
                      ) : (
                        <span className="text-gray-700">{row.config_value}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-2">
                        {isEdit ? (
                          <>
                            <button onClick={save} disabled={saving} className={`${btnBase} bg-green-600 text-white hover:bg-green-700 px-2 py-1`}>
                              Save
                            </button>
                            <button onClick={cancel} className={`${btnBase} text-gray-600 hover:bg-gray-100 px-2 py-1`}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(row)} className={`${btnBase} text-indigo-600 hover:bg-indigo-50 px-2 py-1`}>
                              Edit
                            </button>
                            <button onClick={() => setConfirm(row)} className={`${btnBase} text-red-600 hover:bg-red-50 px-2 py-1`}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete config?"
          message={`"${confirm.config_key}" delete thai jashe. Aa undo nahi thay.`}
          onConfirm={remove}
          onCancel={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

// ============================================================================
// MASTER TAB
// ============================================================================
function MasterTab({ type }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', form }
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // row pending delete
  const [selected, setSelected] = useState(() => new Set()); // ids checked for bulk delete
  const [bulkConfirm, setBulkConfirm] = useState(false); // bulk delete pending confirmation

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/planner/admin/master?type=${encodeURIComponent(type)}`);
      setRows(toArray(data));
      setSelected(new Set()); // reset selection on every (re)load
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = (preset = {}) =>
    setModal({
      mode: "create",
      form: { ...EMPTY_MASTER, type, ...preset },
    });

  const openEdit = (row) =>
    setModal({
      mode: "edit",
      id: row.id,
      form: { ...EMPTY_MASTER, ...normaliseRow(row) },
    });

  const save = async () => {
    const { form, mode, id } = modal;
    // Only validate the fields actually shown for this type.
    const fields = getModalFields(form.type);
    for (const f of fields) {
      if (f.required && !String(form[f.key] ?? "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = cleanMasterPayload(form);
      // item_key is required by the backend; when it isn't shown for this type
      // (e.g. DURATION_OPTION) derive it from the title so the user need not.
      if (!payload.item_key) {
        payload.item_key = slugifyKey(form.title) || null;
      }
      if (mode === "create") {
        await apiFetch("/planner/admin/master", { method: "POST", body: payload });
        toast.success("Item created");
      } else {
        await apiFetch(`/planner/admin/master/${id}`, { method: "PUT", body: payload });
        toast.success("Item updated");
      }
      setModal(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const row = confirm;
    if (!row) return;
    try {
      await apiFetch(`/planner/admin/master/${row.id}`, { method: "DELETE" });
      toast.success("Item deleted");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const bulkRemove = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setSaving(true);
    try {
      await apiFetch("/planner/admin/master/bulk-delete", {
        method: "DELETE",
        body: { ids },
      });
      toast.success(`${ids.length} item(s) deleted`);
      setBulkConfirm(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`${card} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-gray-800">
            {TYPE_LABELS[type] || type}{" "}
            <span className="text-gray-400 font-normal">({rows.length})</span>
          </h2>
          <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
            {type}
          </span>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <button
              onClick={() => setBulkConfirm(true)}
              className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}
            >
              Delete selected ({selected.size})
            </button>
          )}
          <button onClick={load} className={`${btnBase} text-gray-600 hover:bg-gray-100`}>
            Refresh
          </button>
          <button
            onClick={() => openCreate()}
            className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}
          >
            + Add Item
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-400">Loading…</div>
      ) : type === "TOUR" ? (
        <WeekdayGroups
          rows={rows}
          selected={selected}
          onToggle={toggleOne}
          onAdd={(weekday) => openCreate({ weekday })}
          onEdit={openEdit}
          onDelete={setConfirm}
        />
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-gray-400">No items.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                    title="Select all"
                  />
                </th>
                <th className="px-3 py-2 font-medium">ID</th>
                <th className="px-3 py-2 font-medium">Type</th>
                {/* <th className="px-3 py-2 font-medium">Item Key</th> */}
                <th className="px-3 py-2 font-medium">Title</th>
                <th className="px-3 py-2 font-medium">venue</th>
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Weekday</th>
                <th className="px-3 py-2 font-medium">Order</th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={selected.has(row.id) ? "bg-indigo-50/50" : "hover:bg-gray-50"}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                    />
                  </td>
                  <td className="px-3 py-2 text-gray-400">{row.id}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {row.type}
                    </span>
                  </td>
                  {/* <td className="px-3 py-2 text-gray-600">{row.item_key}</td> */}
                  <td className="px-3 py-2 font-medium text-gray-800">{row.title}</td>
                  <td className="px-3 py-2 font-medium text-gray-800">{row.venue}</td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                    {fmtTimeRange(row.start_time, row.end_time)}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{row.weekday || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{row.order_id}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                        isTruthy(row.is_active)
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {isTruthy(row.is_active) ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(row)} className={`${btnBase} text-indigo-600 hover:bg-indigo-50 px-2 py-1`}>
                        Edit
                      </button>
                      <button onClick={() => setConfirm(row)} className={`${btnBase} text-red-600 hover:bg-red-50 px-2 py-1`}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <MasterModal
          modal={modal}
          saving={saving}
          onChange={(form) => setModal({ ...modal, form })}
          onSave={save}
          onClose={() => setModal(null)}
        />
      )}

      {bulkConfirm && (
        <ConfirmDialog
          title={`Delete ${selected.size} item(s)?`}
          message={`Selected ${selected.size} item(s) delete thai jashe. Aa undo nahi thay.`}
          onConfirm={bulkRemove}
          onCancel={() => setBulkConfirm(false)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete item?"
          message={`"${confirm.title}" (${confirm.item_key}) delete thai jashe. Aa undo nahi thay.`}
          onConfirm={remove}
          onCancel={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Reusable confirmation dialog — delete fakt "Yes, delete" click pachi j thay.
// ----------------------------------------------------------------------------
function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-5 py-4">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-600">{message}</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onCancel} className={`${btnBase} text-gray-600 hover:bg-gray-100`}>
            Cancel
          </button>
          <button onClick={onConfirm} className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}>
            Yes, delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Weekday-grouped view (used for TOUR). One card per weekday with an Add button
// that pre-fills the create form with that weekday; rows listed under their day.
// ----------------------------------------------------------------------------
function WeekdayGroups({ rows, selected, onToggle, onAdd, onEdit, onDelete }) {
  // Bucket rows by weekday. Anything without a recognised weekday lands in "OTHER".
  const groups = {};
  for (const wd of WEEKDAYS) groups[wd] = [];
  const other = [];
  for (const row of rows) {
    const wd = (row.weekday || "").toUpperCase();
    if (groups[wd]) groups[wd].push(row);
    else other.push(row);
  }
  // Sort each day's tours by order_id, then start_time.
  const sortRows = (list) =>
    [...list].sort(
      (a, b) =>
        (a.order_id ?? 0) - (b.order_id ?? 0) ||
        String(a.start_time || "").localeCompare(String(b.start_time || ""))
    );

  const dayCard = (wd, list) => (
    <div key={wd} className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
        <h3 className="font-semibold text-gray-800 text-sm">
          {titleCase(wd)}{" "}
          <span className="text-gray-400 font-normal">({list.length})</span>
        </h3>
        <button
          onClick={() => onAdd(wd)}
          className="text-indigo-600 hover:bg-indigo-50 rounded-lg px-2 py-1 text-sm font-medium"
          title={`Add tour on ${titleCase(wd)}`}
        >
          + Add
        </button>
      </div>
      <div className="p-2 space-y-1">
        {list.length === 0 ? (
          <p className="px-2 py-3 text-center text-xs text-gray-400">No tours</p>
        ) : (
          sortRows(list).map((row) => (
            <div
              key={row.id}
              className={`group flex items-center gap-2 rounded-lg px-2 py-1.5 ${
                selected.has(row.id) ? "bg-indigo-50" : "hover:bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(row.id)}
                onChange={() => onToggle(row.id)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 tabular-nums">
                {trimTime(row.start_time) || "--:--"}
              </span>
              <span className="flex-1 truncate text-sm text-gray-800" title={row.title}>
                {row.title}
              </span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => onEdit(row)}
                  className="text-indigo-600 hover:bg-indigo-100 rounded px-1.5 py-0.5 text-xs"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(row)}
                  className="text-red-600 hover:bg-red-100 rounded px-1.5 py-0.5 text-xs"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {WEEKDAYS.map((wd) => dayCard(wd, groups[wd]))}
      {other.length > 0 && dayCard("OTHER (no weekday)", other)}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Master create/edit modal
// ----------------------------------------------------------------------------
function MasterModal({ modal, saving, onChange, onSave, onClose }) {
  const { form, mode } = modal;
  const set = (key, value) => onChange({ ...form, [key]: value });
  const fields = getModalFields(form.type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">
            {mode === "create" ? "Add Master Item" : `Edit Item #${modal.id}`}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <Field
              key={f.key}
              label={`${f.label}${f.required ? " *" : ""}`}
              className={f.input === "textarea" ? "sm:col-span-2" : ""}
            >
              {f.input === "select" ? (
                <select value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className={inputCls}>
                  {f.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "" ? "— none —" : opt}
                    </option>
                  ))}
                </select>
              ) : f.input === "textarea" ? (
                <textarea
                  rows={2}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={inputCls}
                />
              ) : f.input === "bit" ? (
                <label className="inline-flex items-center gap-2 h-[38px]">
                  <input
                    type="checkbox"
                    checked={isTruthy(form[f.key])}
                    onChange={(e) => set(f.key, e.target.checked ? 1 : 0)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                  />
                  <span className="text-sm text-gray-600">Yes</span>
                </label>
              ) : (
                <input
                  type={f.input === "number" ? "number" : "text"}
                  value={form[f.key] ?? ""}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={inputCls}
                />
              )}
            </Field>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className={`${btnBase} text-gray-600 hover:bg-gray-100`}>
            Cancel
          </button>
          <button onClick={onSave} disabled={saving} className={`${btnBase} bg-indigo-600 text-white hover:bg-indigo-700`}>
            {saving ? "Saving…" : mode === "create" ? "Create" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Small helpers / presentational bits
// ----------------------------------------------------------------------------
const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none";

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function isTruthy(v) {
  return v === 1 || v === true || v === "1" || v === "true";
}

// "MONDAY" -> "Monday" (leaves already-mixed strings like "OTHER (no weekday)" readable).
function titleCase(s) {
  return String(s).replace(/\b([A-Z])([A-Z]+)\b/g, (_, a, b) => a + b.toLowerCase());
}

// TIME columns come back as "09:00:00" (or sometimes a full datetime). Trim to HH:MM.
function trimTime(t) {
  if (!t) return "";
  const m = String(t).match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : String(t);
}

function fmtTimeRange(start, end) {
  const s = trimTime(start);
  const e = trimTime(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || "—";
}

// Map a fetched row onto the editable form shape (trims TIME values, coerces bits).
function normaliseRow(row) {
  return {
    type: row.type ?? "",
    item_key: row.item_key ?? "",
    title: row.title ?? "",
    start_time: trimTime(row.start_time),
    end_time: trimTime(row.end_time),
    weekday: row.weekday ?? "",
    duration_mins: row.duration_mins ?? "",
    venue: row.venue ?? "",
    note: row.note ?? "",
    image_path: row.image_path ?? "",
    is_open_area: isTruthy(row.is_open_area) ? 1 : 0,
    order_id: row.order_id ?? 0,
    is_active: isTruthy(row.is_active) ? 1 : 0,
  };
}

// ============================================================================
// LOGIN GATE (frontend-only)
// ============================================================================
function PlannerLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (username.trim() === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      localStorage.setItem(AUTH_STORAGE_KEY, String(Date.now() + AUTH_TTL_MS));
      setError("");
      onSuccess();
    } else {
      setError("Invalid username or password.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Plus_Jakarta_Sans'] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
      >
        <h1 className="text-xl font-bold text-gray-900">Planner Admin</h1>
        <p className="text-sm text-gray-500 mb-6">Sign in to continue</p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Username
        </label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        <button
          type="submit"
          className="w-full py-2 rounded-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition"
        >
          Login
        </button>
      </form>
    </div>
  );
}

export default PlannerAdmin;
